import { isEmpty, isArray } from 'lodash';
import { normalize } from 'normalizr';
import { CONSTANTS } from 'services/constants';
import { createSelector } from 'reselect';
import { putDatabase } from '../actions/database.actions';

export function buildRepositoryAction(repository, action, args)
{
    return { repository, action, args };
}

export function containsIncludes(newIncludes, currentIncludes)
{
    let includes = newIncludes;

    if (isEmpty(includes))
    {
        return true;
    }

    if (isEmpty(currentIncludes))
    {
        return false;
    }

    if (!isArray(newIncludes))
    {
        includes = [includes];
    }

    return includes
        .filter(i => !currentIncludes.includes(i))
        .length === 0;
}

export function loadEntity(id, includes, metaSelectorBuilder, actionCreators, repository, entitySchema)
{
    return async (dispatch, getState) =>
    {
        const metaEntitySelector = metaSelectorBuilder();
        const metaEntity = metaEntitySelector(getState(), id);

        if (metaEntity.loading && containsIncludes(includes, metaEntity.includes))
        {
            return;
        }

        dispatch(actionCreators._loading(id, true, includes));

        const r = await repository.find(id, includes);

        if (!r.isSuccess())
        {
            dispatch(actionCreators._error(id, r.mergeMessages()));
            return r; // eslint-disable-line consistent-return
        }

        const n = normalize(r.getData(), entitySchema);
        dispatch(putDatabase(n.entities));

        dispatch(actionCreators._loading(id, false));

        return r; // eslint-disable-line consistent-return
    };
}

export function saveEntity(id, actionCreators, repositoryData, entitySchema)
{
    return async (dispatch) =>
    {
        dispatch(actionCreators._loading(id || CONSTANTS.DATA.NEW_ID, true));

        const r = await repositoryData.repository[repositoryData.action](...repositoryData.args);

        if (!r.isSuccess())
        {
            dispatch(actionCreators._error(id || CONSTANTS.DATA.NEW_ID, r.mergeMessages()));
        }
        else
        {
            const n = normalize(r.getData(), entitySchema);
            dispatch(putDatabase(n.entities));

            dispatch(actionCreators._loading(id || CONSTANTS.DATA.NEW_ID, false));
        }

        return r;
    };
}

export function loadAll(metaSelectorBuilder, actionCreators, repository, entitySchema)
{
    return async (dispatch, getState) =>
    {
        const id = CONSTANTS.DATA.ALL_ID;
        const metaEntitySelector = metaSelectorBuilder();
        const metaEntity = metaEntitySelector(getState(), id);

        if (metaEntity.loading)
        {
            return;
        }

        dispatch(actionCreators._loading(id, true));

        const r = await repository.all();

        if (!r.isSuccess())
        {
            dispatch(actionCreators._error(id, r.mergeMessages()));
            return r; // eslint-disable-line consistent-return
        }

        const n = normalize(r.getData(), [entitySchema]);

        dispatch(putDatabase(n.entities));

        dispatch(actionCreators._loading(id, false));
        dispatch(actionCreators._loaded(id, true));

        return r; // eslint-disable-line consistent-return
    };
}

export function loadAllWithLocal(metaSelectorBuilder, selector, actionCreators, repository, entitySchema)
{
    return async (dispatch, getState) =>
    {
        const metaEntitySelector = createSelector([selector()], items => items);
        const metaEntity = metaEntitySelector(getState());

        if (!isEmpty(metaEntity))
        {
            return;
        }

        dispatch(loadAll(metaSelectorBuilder, actionCreators, repository, entitySchema));
    };
}

export function loadHotelPromotions(metaSelectorBuilder, actionCreators, repository, entitySchema)
{
    return async (dispatch, getState) =>
    {
        const metaEntitySelector = metaSelectorBuilder();
        const metaEntity = metaEntitySelector(getState(), CONSTANTS.DATA.ALL_ID);

        if (metaEntity.loading)
        {
            return;
        }

        dispatch(actionCreators._loading(CONSTANTS.DATA.ALL_ID, true));

        const r = await repository.offers();

        if (!r.isSuccess())
        {
            dispatch(actionCreators._error(id, r.mergeMessages()));
            return r; // eslint-disable-line consistent-return
        }

        const n = normalize(r.getData(), [entitySchema]);

        dispatch(putDatabase(n.entities));

        dispatch(actionCreators._loading(CONSTANTS.DATA.ALL_ID, false));

        return r; // eslint-disable-line consistent-return
    };
}

export function loadEntityBySlug(slug, includes, metaSelectorBuilder, actionCreators, repository, entitySchema)
{
    return async (dispatch, getState) =>
    {
        const metaEntitySelector = metaSelectorBuilder();
        const metaEntity = metaEntitySelector(getState(), slug);

        if (metaEntity.loading && containsIncludes(includes, metaEntity.includes))
        {
            return;
        }

        dispatch(actionCreators._loading(slug, true, includes));

        const r = await repository.getBySlug(slug);

        if (!r.isSuccess())
        {
            dispatch(actionCreators._error(slug, r.mergeMessages()));
            return r; // eslint-disable-line consistent-return
        }

        const n = normalize(r.getData(), entitySchema);
        dispatch(putDatabase(n.entities));

        dispatch(actionCreators._loading(slug, false));

        return r; // eslint-disable-line consistent-return
    };
}

export function loadEntityHotelsFeatured(metaSelectorBuilder, actionCreators, repository, entitySchema)
{
    return async (dispatch, getState) =>
    {
        const id = CONSTANTS.DATA.ALL_ID;
        const metaEntitySelector = metaSelectorBuilder();
        const metaEntity = metaEntitySelector(getState(), id);

        if (metaEntity.loading)
        {
            return;
        }

        dispatch(actionCreators._loading(id, true));

        const r = await repository.featured();

        if (!r.isSuccess())
        {
            dispatch(actionCreators._error(id, r.mergeMessages()));
            return r; // eslint-disable-line consistent-return
        }

        const n = normalize(r.getData(), [entitySchema]);

        dispatch(putDatabase(n.entities));

        dispatch(actionCreators._loading(id, false));

        return r; // eslint-disable-line consistent-return
    };
}

export function loadEntityHotelsLatest(metaSelectorBuilder, actionCreators, repository, entitySchema)
{
    return async (dispatch, getState) =>
    {
        const id = CONSTANTS.DATA.ALL_ID;
        const metaEntitySelector = metaSelectorBuilder();
        const metaEntity = metaEntitySelector(getState(), id);

        if (metaEntity.loading)
        {
            return;
        }

        dispatch(actionCreators._loading(id, true));

        const r = await repository.latest();

        if (!r.isSuccess())
        {
            dispatch(actionCreators._error(id, r.mergeMessages()));
            return r; // eslint-disable-line consistent-return
        }

        const n = normalize(r.getData(), [entitySchema]);

        dispatch(putDatabase(n.entities));

        dispatch(actionCreators._loading(id, false));

        return r; // eslint-disable-line consistent-return
    };
}

export function loadDestinationEntityBySlug(slug, includes, metaSelectorBuilder, actionCreators, repository, entitySchema)
{
    return async (dispatch, getState) =>
    {
        const metaEntitySelector = metaSelectorBuilder();
        const metaEntity = metaEntitySelector(getState(), slug);

        if (metaEntity.loading && containsIncludes(includes, metaEntity.includes))
        {
            return;
        }

        dispatch(actionCreators._loading(slug, true, includes));

        const r = await repository.getDestinationBySlug(slug);

        if (!r.isSuccess())
        {
            dispatch(actionCreators._error(slug, r.mergeMessages()));
            return r; // eslint-disable-line consistent-return
        }

        const n = normalize(r.getData(), entitySchema);
        dispatch(putDatabase(n.entities));

        dispatch(actionCreators._loading(slug, false));

        return r; // eslint-disable-line consistent-return
    };
}

export function loadAllD(metaSelectorBuilder, actionCreators, repository, entitySchema)
{
    return async (dispatch, getState) =>
    {
        const metaEntitySelector = metaSelectorBuilder();
        const metaEntity = metaEntitySelector(getState(), CONSTANTS.DATA.ALL_ID);

        if (metaEntity.loading)
        {
            return;
        }

        dispatch(actionCreators._loading(CONSTANTS.DATA.ALL_ID, true));

        const r = await repository.all();

        if (!r.isSuccess())
        {
            dispatch(actionCreators._error(id, r.mergeMessages()));
            return r; // eslint-disable-line consistent-return
        }

        const n = normalize(r.getData(), [entitySchema]);

        dispatch(putDatabase(n.entities));

        dispatch(actionCreators._loading(CONSTANTS.DATA.ALL_ID, false));

        return r; // eslint-disable-line consistent-return
    };
}
